import { CurrencyPipe, DatePipe, DecimalPipe, KeyValuePipe, PercentPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, inject, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjChartAnnotationModule } from '@grapecity/wijmo.angular2.chart.annotation'
import { WjGaugeModule } from '@grapecity/wijmo.angular2.gauge'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { AllowSorting, CellEditEndingEventArgs, CellType, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { Menu } from '@grapecity/wijmo.input'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    BonusTableYearlyVariable,
    FeatureType,
    Grid,
    ListItem,
    ModalSize,
    ViewportService,
    replaceListItemsIfChanged,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BonusTableYearlyVariableComponent } from './bonus-table-yearly-variables/bonus-table-yearly-variable.component'
import { CostChangesComponent } from './cost-changes/cost-changes'
import { JobTypeSummaryComponent } from './job-type-summary/job-type-summary.component'

@Component({
    templateUrl: './projections.component.html',
    styleUrls: ['./projections.component.css'],
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        WjInputModule,
        FormsModule,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        WjGaugeModule,
        WjChartModule,
        WjChartAnnotationModule,
        FaIconComponent,
        NgbNavContent,
        NgbNavOutlet,
        CostChangesComponent,
        JobTypeSummaryComponent,
        DecimalPipe,
        CurrencyPipe,
        DatePipe,
        KeyValuePipe,
    ],
})
export class ProjectionsComponent extends BaseComponent {
    private static readonly JOB_STATUS_STORAGE_KEY = 'projections-job-status'
    private static readonly YEARS_STORAGE_KEY = 'projections-years'

    @ViewChild('ctxMenu') ctxMenu!: Menu
    menuItems = [] as any[]
    currencyPipe = new CurrencyPipe('en-US')

    faSpinner = faSpinner
    active = 1
    activeInner = 1
    grid: Grid<any>
    bonusGrid: Grid<any>
    bonusInputGrid: Grid<BonusTableYearlyVariable>
    workoffsGrid: Grid<any>
    costTypeCostsGrid: Grid<any>
    costTypeCostsGridValidation: Grid<any>
    validationCostsGrid: Grid<any>
    noRevGrid: Grid<any>
    earnedVarianceGrid: Grid<any>
    scrollPosition: any
    private generateColumnsTimeout: ReturnType<typeof setTimeout> | undefined
    private persistedYearSelection: number[] | null = null

    activeMap: ListItem<number>[] = [
        new ListItem(1, 'Active'),
        new ListItem(2, 'Soft Closed'),
        new ListItem(3, 'Hard Closed'),
        new ListItem(0, 'Pending'),
    ]

    isOpenMonthBilled = false
    yearMap: ListItem<number>[] = [new ListItem(2023, '2023'), new ListItem(2024, '2024'), new ListItem(2025, '2025')]
    selectedYear = 2023
    monthCols = [] as any[]

    byJobByJobType: any = {}
    private static readonly NO_GROUPING = 'none'
    selectedGrouping = 'jobType'
    groupingOptions: ListItem<string>[] = [
        new ListItem('jobType', 'Job Type'),
        new ListItem('customer', 'Customer'),
        new ListItem(ProjectionsComponent.NO_GROUPING, 'No Grouping'),
        new ListItem('udPreQualified', 'PreQualified'),
        new ListItem('udSpecialized', 'Specialized'),
        new ListItem('projectManager', 'Project Manager'),
        new ListItem('siteSuper', 'Site Super'),
        new ListItem('pe', 'PE'),
        new ListItem('jca', 'JCA'),
        new ListItem('estimator', 'Estimator'),
    ]

    allDates: Date[] = []
    rawData: any[] = []
    allRevenueData: any[] = []
    dynamicColumns: any[] = []
    dynamicRows: any[] = []
    availableYears: ListItem<number>[] = []
    currentYear = 0

    availableMonths: ListItem<number>[] = []

    costTypeCostMonthCols = [] as any[]

    // Gauge Properties
    progress = 0
    progress2 = 0
    ranges: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0]
    gaugeDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    currentYearTotalActualProfit = 0
    projectedProfit = 0
    nextYearProgress = 0
    nextYearGP = 0

    // Cost Chart Properties
    costChartData: any[] = []
    activeJobsMap: ListItem<string>[] = []
    activeJobsMap2: ListItem<string>[] = []
    showDataLabels = true
    costHourMap: ListItem<number>[] = [
        new ListItem(0, 'Show Costs'),
        new ListItem(1, 'Show Hours (CurrEst)'),
        new ListItem(2, 'Show Hours (Proj)'),
    ]
    costHourType = 0
    isCost = true

    // Phase Chart Properties
    availablePhases: ListItem<string>[] = []
    hourCostMap: ListItem<boolean>[] = [new ListItem(true, 'Show Hours'), new ListItem(false, 'Show Costs')]
    isHours = true
    phaseLoadComplete = false
    openMonth = new Date(2023, 1, 1)
    projectionStartMonth: Date | null = null
    get OpenMonthText() {
        const datePipe = new DatePipe('en-US')
        const mth = datePipe.transform(this.openMonth, 'MMM yyyy') as string
        return 'Open Month ' + mth + ''
    }
    get ProjectionStartMonthText() {
        if (!this.projectionStartMonth) return ''
        const datePipe = new DatePipe('en-US')
        return datePipe.transform(this.projectionStartMonth, 'MMM yyyy') as string
    }
    get projectionStartMonthWarning(): string {
        if (!this.projectionStartMonth) return ''
        if (this.projectionStartMonth > this.openMonth)
            return 'Projection Start Month is beyond the open WIP month. Actuals are limited to closed months only.'
        return ''
    }

    groupedPhaseData!: any
    private viewport = inject(ViewportService)
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }
    get frozenColumns(): number {
        if (this.viewport.isMobile()) return 0
        return this.active != 3 ? 2 : 4
    }
    constructor() {
        super()

        this.restoreJobStatusSelection()
        this.persistedYearSelection = this.readStoredNumbers(ProjectionsComponent.YEARS_STORAGE_KEY)

        this.grid = new Grid<any>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.jobName])
            .isReadOnly(false)
            .allowSorting(AllowSorting.None)
            .editOnDoubleClick(false)
        this.grid.initialized.on(() => this.gridInitialized())

        this.bonusGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders().isReadOnly(true).editOnDoubleClick(false)

        this.bonusInputGrid = new Grid<BonusTableYearlyVariable>(this.services, 'id', this)
            .setupModalEditing(
                this.services.data.bonusTableYearlyVariables,
                'Monthly Plan',
                BonusTableYearlyVariableComponent,
                () => new BonusTableYearlyVariable(),
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders()
        this.bonusInputGrid.modified.on(this.refreshBonus.bind(this))

        this.workoffsGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.costTypeCostsGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.costTypeCostsGridValidation = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.validationCostsGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.noRevGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.earnedVarianceGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()

        this.refresh()
        this.refreshLaborPhases()
    }
    gridInitialized() {
        const grid = this.grid.flexGrid as FlexGrid
        const host = grid.hostElement
        host.addEventListener('contextmenu', (e: MouseEvent) => {
            const ht = grid.hitTest(e)
            const panel = ht.panel
            if (panel.cellType === CellType.Cell) {
                const row = panel.grid.rows[ht.row]
                const col = panel.grid.columns[ht.col] as any
                const rowItem = row.dataItem
                console.log(rowItem)
                if (this.active == 3) {
                    e.preventDefault()
                    if (!rowItem.jobId) return
                    const bindingParts = col._binding._parts
                    if (bindingParts.length === 3 && rowItem.detailsByMonth) {
                        const secondBinding =
                            bindingParts[1].indexOf(' Billed') > -1 ? bindingParts[1].replace(' Billed', '') : bindingParts[1]
                        const match = rowItem[bindingParts[0]][secondBinding]

                        // We only want the menu to appear on certain cells.
                        if (!match) return
                        if (!match.date) return
                        if (match.isRevenueActual && match.date.toDateString() != this.openMonth.toDateString()) return
                        if (match.date.toDateString() == this.openMonth.toDateString() && this.isOpenMonthBilled) return

                        console.log(match)
                        this.menuItems = [
                            {
                                header: match.isYellow ? 'Show White' : 'Show Yellow',
                                cmd: {
                                    job: rowItem.jobId,
                                    date: secondBinding,
                                    isYellow: !match.isYellow,
                                    revenue: match.revenue,
                                },
                            },
                        ]
                        this.ctxMenu.selectedItem = undefined
                        this.ctxMenu.show(e)
                    }
                }
            }
        })
    }
    menuItemClicked(menu: Menu) {
        console.log(menu.selectedValue)
        const match = this.rawData.filter(x => x.job == menu.selectedValue.job && x.binding == menu.selectedValue.date)[0]
        if (match) {
            match.isYellow = menu.selectedValue.isYellow
        }
        this.updateBilledProjection(
            menu.selectedValue.job,
            menu.selectedValue.date,
            menu.selectedValue.revenue,
            menu.selectedValue.isYellow ?? false,
        )
        this.generateRows()
        //alert('Executing **' + menu.selectedValue);
    }
    refreshLaborPhases() {
        this.cancelableRequest(
            'laborPhases',
            true,
            this.services.data.load(false, true, this.services.data.get('/api/Projections/GetAvailablePhases')).subscribe({
                next: result => {
                    const availablePhases = result[0].map(x => new ListItem(x.phase, x.phase + ' ' + x.description)) as ListItem<string>[]
                    this.availablePhases = availablePhases
                    this.phaseLoadComplete = true
                },
            }),
        )
    }
    refreshEarnedRevenueVariances() {
        let params = new HttpParams()
        if (this.projectionStartMonth) {
            params = params.set('projectionStartMonth', this.projectionStartMonth.toISOString())
        }
        this.cancelableRequest(
            'earnedRevenueVariances',
            true,
            this.services.data
                .load(false, true, this.services.data.get('/api/Projections/GetEarnedRevenueVariances', params))
                .subscribe({
                    next: result => {
                        this.earnedVarianceGrid.items = result[0] ?? []
                    },
                }),
        )
    }
    refreshCostData() {
        this.costChartData = []
        this.isCost = this.costHourType == 0
        const requestDto = {
            jobs: this.activeJobsMap.filter(x => x.selected).map(x => x.value),
            jobCount: this.activeJobsMap.length,
            phases: [],
            costHourType: this.costHourType,
            jobStatusString: this.activeMap
                .filter(item => item.$checked)
                .map(x => x.value)
                .join(','),
            projectionStartMonth: this.projectionStartMonth,
        }
        this.cancelableRequest(
            'Costs',
            true,
            this.services.data.load(true, true, this.services.data.post('/api/Projections/GetCostData', requestDto)).subscribe({
                next: result => {
                    this.costChartData = result[0].costReportDtos
                    this.costTypeCostsGridValidation.items = result[0].costValidations
                    this.transposeCostChartDataToGrid()
                    this.noRevGrid.items = result[0].jobsWithoutProj
                    this.workoffsGrid.items = result[0].workOffRecords
                    this.validationCostsGrid.items = result[0].costRecords ?? []
                },
            }),
        )
    }
    refreshPhaseData() {
        const requestDto = {
            jobs: this.activeJobsMap2.filter(x => x.selected).map(x => x.value),
            jobCount: this.activeJobsMap2.length,
            phases: this.availablePhases.filter(x => x.selected).map(x => x.value),
            projectionStartMonth: this.projectionStartMonth,
        }
        this.cancelableRequest(
            'Costs',
            true,
            this.services.data.load(true, true, this.services.data.post('/api/Projections/GetLaborPhaseCostData', requestDto)).subscribe({
                next: result => {
                    const phaseCosts = result[0].costReportDtos as any[]
                    const grouped = phaseCosts.toObjectGroups(
                        x => x.phase,
                        x => x,
                    )
                    this.groupedPhaseData = grouped
                },
            }),
        )
    }
    refresh() {
        let params = new HttpParams().set(
            'jobStatusString',
            this.activeMap
                .filter(item => item.$checked)
                .map(x => x.value)
                .join(','),
        )
        if (this.projectionStartMonth) {
            params = params.set('projectionStartMonth', this.projectionStartMonth.toISOString())
        }
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/Projections/GetProjectionsData', params),
                    this.services.data.bonusTableYearlyVariables.queryAll(),
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.get('/api/Projections/GetOpenMonth'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.openMonth = new Date(result[3])

                        // Initialize projectionStartMonth to the WIP open month on first load.
                        if (!this.projectionStartMonth) {
                            this.projectionStartMonth = new Date(this.openMonth)
                        }

                        this.rawData = result[0]

                        // Rebuild the Costs/Hours job list from the unique jobs in the revenue data,
                        // so the available jobs always match the jobs shown on the revenue tabs
                        // (filtered by the selected Job Status).
                        const previousSelections = new Set(this.activeJobsMap.filter(x => x.selected).map(x => x.value))
                        const uniqueJobs = new Map<string, string>()
                        for (const d of this.rawData) {
                            if (!uniqueJobs.has(d.job)) {
                                uniqueJobs.set(d.job, d.description)
                            }
                        }
                        this.activeJobsMap = Array.from(uniqueJobs.entries()).map(([id, desc]) => {
                            const item = new ListItem(id, id + ' - ' + desc)
                            item.selected = previousSelections.has(id)
                            return item
                        })

                        // Labor Phase tab uses all jobs from vpJobs (not filtered by status).
                        if (this.activeJobsMap2.length == 0) {
                            this.activeJobsMap2 = this.services.data.vpJobs.items.map(x => new ListItem(x.id, x.id + ' - ' + x.name))
                        }

                        this.prepareData()
                        this.bonusInputGrid.items = this.services.data.bonusTableYearlyVariables.items

                        const years = this.services.data.bonusTableYearlyVariables.items.map(x => x.year)
                        const yearMap = [] as ListItem<number>[]
                        for (const year of years) {
                            yearMap.push(new ListItem(year, year.toString()))
                        }
                        this.yearMap = yearMap
                        this.refreshBonus()
                        this.calculateProgress()

                        // If on the Costs/Hours tab, refresh cost data with the updated job list.
                        if (this.active === 7) {
                            this.refreshCostData()
                        }
                    },
                }),
        )
    }
    prepareData() {
        // Loop through the data and set the binding (e.g. 'Jan 23') from the month date.
        const rawData = this.rawData
        for (const d of rawData) {
            d.binding = this.monthByInt.find(x => x.val == d.date.getMonth())?.display + ' ' + (d.date.getFullYear() - 2000)
        }

        this.allRevenueData = rawData

        if (!rawData.length) {
            this.byJobByJobType = {}
            this.generateColumns()
            return
        }

        // Let's get all the available years in the data.
        const dates = rawData.map(x => x.date).orderBy(x => x)
        let year = dates[0].getFullYear()
        const lastYear = dates[dates.length - 1].getFullYear()
        const previouslyChecked = new Set(this.availableYears.filter(x => x['checked']).map(x => x.value))
        if (previouslyChecked.size === 0 && this.persistedYearSelection?.length) {
            for (const storedYear of this.persistedYearSelection) {
                previouslyChecked.add(storedYear)
            }
        }
        const yearsInRange: number[] = []
        while (year <= lastYear) {
            yearsInRange.push(year)
            year++
        }
        const overlappingYears = yearsInRange.filter(y => previouslyChecked.has(y))
        const currentYear = new Date().getFullYear()
        const yearsToCheck = new Set(overlappingYears.length > 0 ? overlappingYears : [currentYear])
        const availableYears = [] as ListItem<number>[]
        for (const availableYear of yearsInRange) {
            const item = new ListItem(availableYear, availableYear.toString())
            item['checked'] = yearsToCheck.has(availableYear)
            availableYears.push(item)
        }
        this.availableYears = replaceListItemsIfChanged(this.availableYears, availableYears.orderByDescending(x => x.value))

        const availableMonths = [] as ListItem<number>[]
        const currentMonth = new Date().getMonth()
        for (let i = 0; i < this.monthByInt.length; i++) {
            const item = new ListItem(i, this.monthByInt[i].display)
            item['checked'] = false
            if (i > currentMonth - 3) {
                item['checked'] = true
            }
            availableMonths.push(item)
        }
        this.availableMonths = availableMonths

        this.rebuildJobGroups()
        this.generateColumns()
    }
    private rebuildJobGroups() {
        const isUngrouped = this.selectedGrouping === ProjectionsComponent.NO_GROUPING
        const dataByGroup = this.rawData.toObjectGroups(
            x => (isUngrouped ? '' : x[this.selectedGrouping]),
            x => x,
        )

        for (const prop in dataByGroup) {
            const jobs = dataByGroup[prop].toObjectGroups(
                x => x.job,
                x => x,
            ) as any
            if (isUngrouped) {
                const sorted = {} as any
                const jobNumbers = Object.keys(jobs).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                for (const jobNumber of jobNumbers) {
                    sorted[jobNumber] = jobs[jobNumber]
                }
                dataByGroup[prop] = sorted
            } else {
                dataByGroup[prop] = jobs
            }
        }
        this.byJobByJobType = dataByGroup
    }

    dateToColHeader(date: Date) {
        return this.monthByInt.find(x => x.val == date.getMonth())?.display + ' ' + (date.getFullYear() - 2000)
    }
    generateColumns() {
        if (!this.grid.flexGrid) return
        this.scrollPosition = this.grid.flexGrid.scrollPosition

        // We need to clear the columns to force the grid to reset its collection, and then regenerate the columns after a timeout.
        this.monthCols = []
        if (this.generateColumnsTimeout) {
            clearTimeout(this.generateColumnsTimeout)
        }
        this.generateColumnsTimeout = setTimeout(() => {
            this.generateColumnsTimeout = undefined
            const columns = [] as any[]
            const selectedYears = this.availableYears
                .filter(x => x['checked'])
                .map(x => x.value)
                .orderBy(x => x)
            for (const year of selectedYears) {
                for (let j = 0; j < 12; j++) {
                    // For the billed amount report, we need to have two columns for the open month...
                    columns.push(this.monthByInt.find(x => x.val == j)?.display + ' ' + (year - 2000))
                }
                columns.push(year + ' Totals')
            }
            this.monthCols = columns
            this.generateRows()
        }, 10)
    }
    generateRows() {
        this.scrollPosition = this.grid.flexGrid.scrollPosition

        const isUngrouped = this.selectedGrouping === ProjectionsComponent.NO_GROUPING
        const rows = [] as any[]
        const ungroupedTotals = [] as any[]
        for (const prop in this.byJobByJobType) {
            // Track the index of the current group, so we can add the header row in at that index, if the group has values.
            const currentGroupIndex = rows.length

            // Add each job row for the group.  We need to add the detailsByMonth object.
            const jobTypeTotals = [] as any[]
            for (const prop2 in this.byJobByJobType[prop]) {
                const jobMonths = this.byJobByJobType[prop][prop2]
                const row = {
                    job: jobMonths[0].job + ' ' + jobMonths[0].description,
                    jobId: jobMonths[0].job,
                    jobContract: jobMonths[0].jobContract,
                    fillOrTotalRow: false,
                    detailsByMonth: {} as any,
                    billedPlusProjection: 0,
                    remainingToProject: 0,
                }
                const detailsByMonth = {} as any
                let revenueYearTotal = 0

                for (const c of this.monthCols) {
                    const match = this.allRevenueData.find(x => x.job == jobMonths[0].job && x.binding == c)
                    let revenue = 0
                    let billedProjection = 0 // We'll track billed project for the tooltip.
                    switch (this.active) {
                        case 1:
                            revenue = match?.revenue ?? 0
                            break
                        case 2:
                            revenue = match?.grossProfit ?? 0
                            break
                        case 3:
                            // When showing billed revenue, we will show either billedAmount or billed Projection.
                            if (match && match.date) {
                                if (match.date.toDateString() == this.openMonth.toDateString()) {
                                    if (this.isOpenMonthBilled) {
                                        revenue = match?.billedAmount ?? 0
                                    } else {
                                        revenue = match?.billedProjection ?? 0
                                    }
                                } else {
                                    if (match.date > this.openMonth) {
                                        revenue = match?.billedProjection ?? 0
                                    } else {
                                        revenue = match?.billedAmount ?? 0
                                    }
                                }
                                billedProjection = match?.billedProjection ?? 0
                            }
                            break
                        default:
                    }
                    revenueYearTotal += revenue
                    detailsByMonth[c] = {}
                    if (c.indexOf('Totals') > -1) {
                        detailsByMonth[c]['revenue'] = revenueYearTotal

                        // Add or update the job type totals
                        const jobTypeTotalMatch = jobTypeTotals.find(x => x.binding == c)
                        if (!jobTypeTotalMatch) {
                            jobTypeTotals.push({
                                binding: c,
                                revenue: revenueYearTotal,
                            })
                        } else {
                            jobTypeTotalMatch.revenue += revenueYearTotal
                        }

                        // Reset the year total in case we are looking at more than one year of columns.
                        revenueYearTotal = 0
                    } else {
                        detailsByMonth[c]['revenue'] = c.indexOf('Totals') > -1 ? revenueYearTotal : revenue

                        // Add or update the job type totals
                        const jobTypeTotalMatch = jobTypeTotals.find(x => x.binding == c)
                        if (!jobTypeTotalMatch) {
                            jobTypeTotals.push({
                                binding: c,
                                revenue: revenue,
                            })
                        } else {
                            jobTypeTotalMatch.revenue += revenue
                        }
                    }
                    detailsByMonth[c]['isRevenueActual'] = match ? match.isRevenueActual : false
                    detailsByMonth[c]['date'] = match ? match.date : undefined
                    detailsByMonth[c]['isYellow'] = match ? match.isYellow : false
                    detailsByMonth[c]['billedProjection'] = billedProjection
                }
                row.detailsByMonth = detailsByMonth

                // Finally, for the billed revenues, we need to total the revenues and projections across all dates.
                let billedPlusProjection = 0
                for (const month of jobMonths) {
                    if (month.date.toDateString() == this.openMonth.toDateString()) {
                        billedPlusProjection += this.isOpenMonthBilled ? (month?.billedAmount ?? 0) : (month?.billedProjection ?? 0)
                    } else {
                        billedPlusProjection += month.date > this.openMonth ? (month?.billedProjection ?? 0) : (month?.billedAmount ?? 0)
                    }
                }
                row.billedPlusProjection = billedPlusProjection
                row.remainingToProject = row.jobContract - row.billedPlusProjection

                // If the row has no values, and we are not viewing the 'Revenue Billed' tab, we can skip adding the row.
                if (this.hasNonZeroMonthValue(row.detailsByMonth) || this.active === 3) {
                    rows.push(row)
                }
            }

            // If needed, add a totals row that will need the totals by month and year for all jobs looped above.
            const detailsByMonth = {}
            for (const jtt of jobTypeTotals) {
                detailsByMonth[jtt.binding] = { revenue: jtt.revenue, isRevenueActual: false }
            }

            // Add the header and footer rows for the group whenever any job rows were shown,
            // so group totals never drop while jobs in the group are still visible.
            // No Grouping keeps the job rows only; those month totals still feed Grand Totals.
            if (rows.length > currentGroupIndex) {
                if (isUngrouped) {
                    ungroupedTotals.push({ detailsByMonth: detailsByMonth })
                } else {
                    // Add the row that has the group header before the rows we added for the details
                    rows.insertItem(currentGroupIndex, {
                        job: prop == 'null' ? 'Jobs With No Type' : prop,
                        fillOrTotalRow: true,
                    })

                    // Add the totals row for the group
                    rows.push({
                        job: 'Totals - ' + prop,
                        fillOrTotalRow: true,
                        isGroupTotalRow: true,
                        detailsByMonth: detailsByMonth,
                    })

                    // Add a blank row to better distinquish the groups
                    rows.push({
                        job: '',
                        fillOrTotalRow: true,
                    })
                }
            }
        }

        // Now that we have added rows for all jobs, we can get the Totals rows and sum them into a grand total.
        const totalsRows = isUngrouped ? ungroupedTotals : rows.filter(x => x.isGroupTotalRow)
        const totalsByMonth = {} as any
        for (const row of totalsRows) {
            for (const prop in row.detailsByMonth) {
                const detail = row.detailsByMonth[prop]
                if (Object.prototype.hasOwnProperty.call(totalsByMonth, prop)) {
                    totalsByMonth[prop].revenue += detail.revenue
                } else {
                    totalsByMonth[prop] = {
                        revenue: detail.revenue,
                        isRevenueActual: false,
                        date: detail.date,
                    }
                }
            }
        }
        rows.push({
            job: 'Grand Totals',
            detailsByMonth: totalsByMonth,
        })

        // Finally, after the grand totals, we can add rows for each Project Manager's total.
        // group up the data by jobtype for lookup later
        // this.addManagerHeaderRows(rows, 'Project Manager')
        // this.addManagerRows(rows, 'projectManager')
        // this.addManagerHeaderRows(rows, 'Site Super')
        // this.addManagerRows(rows, 'siteSuper')
        // this.addManagerHeaderRows(rows, 'PE')
        // this.addManagerRows(rows, 'pe')
        // this.addManagerHeaderRows(rows, 'JCA')
        // this.addManagerRows(rows, 'jca')

        this.grid.items = rows
        this.grid.flexGrid.scrollPosition = this.scrollPosition
    }
    addManagerHeaderRows(rows, mangerType) {
        rows.push({
            job: '',
            fillOrTotalRow: true,
        })
        rows.push({
            job: 'Totals by ' + mangerType,
            fillOrTotalRow: true,
        })
    }
    addManagerRows(rows, managerProperty) {
        const dataByPm = this.allRevenueData.toObjectGroups(
            x => x[managerProperty],
            x => x,
        )

        const propertyNames = [] as any[]
        for (const prop in dataByPm) {
            if (Object.prototype.hasOwnProperty.call(dataByPm, prop)) {
                propertyNames.push(prop)
            }
        }
        propertyNames.sort()

        for (const prop of propertyNames) {
            // Add the row that has the job type header
            const row = {
                job: prop ?? 'Not Assigned',
                fillOrTotalRow: true,
                detailsByMonth: {} as any,
            }
            const detailsByMonth = {} as any
            let yearlyTotalRevenue = 0

            for (const c of this.monthCols) {
                detailsByMonth[c] = {}
                const match = this.allRevenueData.filter(x => x[managerProperty] == prop && x.binding == c)
                detailsByMonth[c]['date'] = match[0] ? match[0].date : undefined

                let revenue = 0
                switch (this.active) {
                    case 1:
                        if (c.indexOf('Totals') == -1) {
                            revenue = match ? match.reduce((acc, current) => acc + current.revenue, 0) : 0
                        } else if (c.indexOf('Totals') > -1) {
                            detailsByMonth[c]['revenue'] = yearlyTotalRevenue
                            yearlyTotalRevenue = 0
                            continue
                        }
                        break
                    case 2:
                        if (c.indexOf('Totals') == -1) {
                            revenue = match ? match.reduce((acc, current) => acc + current.grossProfit, 0) : 0
                        } else if (c.indexOf('Totals') > -1) {
                            detailsByMonth[c]['revenue'] = yearlyTotalRevenue
                            yearlyTotalRevenue = 0
                            continue
                        }
                        break
                    case 3:
                        // When showing billed revenue, we will show either billedAmount or billed Projection.
                        if (match[0] && match[0].date) {
                            if (match[0].date.toDateString() == this.openMonth.toDateString()) {
                                revenue = this.isOpenMonthBilled
                                    ? match.reduce((acc, current) => acc + current.billedAmount, 0)
                                    : match.reduce((acc, current) => acc + current.billedProjection, 0)
                            } else {
                                revenue =
                                    match[0].date > this.openMonth
                                        ? match.reduce((acc, current) => acc + current.billedProjection, 0)
                                        : match.reduce((acc, current) => acc + current.billedAmount, 0)
                            }
                        } else if (c.indexOf('Totals') > -1) {
                            detailsByMonth[c]['revenue'] = yearlyTotalRevenue
                            yearlyTotalRevenue = 0
                            continue
                        }
                        break
                    default:
                }
                yearlyTotalRevenue += revenue
                detailsByMonth[c]['revenue'] = revenue
            }
            row.detailsByMonth = detailsByMonth
            rows.push(row)
        }
    }
    transposeCostChartDataToGrid() {
        const datePipe = new DatePipe('en-US')

        const data = this.costChartData.slice()
        const monthCols = [] as any[]
        const gridData = [] as any[]

        for (const prop in data[0]) {
            if (prop.toLowerCase().indexOf('month') > -1) continue
            if (prop.toLowerCase().indexOf('phase') > -1) continue
            gridData.push({
                costType: this.costSeries[prop],
                costKey: prop,
                detailsByMonth: {},
            })
        }

        const costTotalsByBinding = {}
        const revenueTotalsByBinding = {}
        const gpTotalsByBinding = {}

        for (const item of data) {
            const binding = datePipe.transform(item.month, 'MMM yyyy') as string
            costTotalsByBinding[binding] = 0
            revenueTotalsByBinding[binding] = item.monthRevenue
            gpTotalsByBinding[binding] = item.monthGP
            monthCols.push(binding)
            for (const prop in item) {
                if (prop.toLowerCase().indexOf('month') > -1) continue
                if (prop.toLowerCase().indexOf('phase') > -1) continue
                gridData.find(x => x.costKey == prop).detailsByMonth[binding] = item[prop] == null ? 0 : item[prop]
                // FS/Non-FS labor are sub-breakdowns of the Labor total, so exclude from cost total to avoid double-counting.
                const isFsSubLabor = prop.indexOf('FsLabor') > -1 || prop.indexOf('NonFsLabor') > -1
                if (!isFsSubLabor) {
                    costTotalsByBinding[binding] += item[prop] == null ? 0 : item[prop]
                }
            }
        }

        gridData.push({ costType: '', detailsByMonth: {} })
        gridData.push({ costType: 'Total Revenue', detailsByMonth: revenueTotalsByBinding })
        gridData.push({ costType: 'Total GP', detailsByMonth: gpTotalsByBinding })
        gridData.push({ costType: 'Total Cost', detailsByMonth: costTotalsByBinding })

        this.costTypeCostMonthCols = monthCols
        this.costTypeCostsGrid.items = gridData
    }
    mainGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.ColumnHeader) {
            cell.style.backgroundColor = ''
            cell.title = ''
            const col = panel.grid.columns[c] as any
            const openMonth =
                this.monthByInt.find(x => x.val == this.openMonth.getMonth())?.display + ' ' + (this.openMonth.getFullYear() - 2000)
            const bindingParts = col._binding._parts
            if (this.active == 3 && bindingParts.length === 3 && bindingParts[1] == openMonth) {
                cell.style.backgroundColor = 'darkorange'
                cell.title = this.OpenMonthText

                const div = document.createElement('span')
                div.style.display = 'block'
                const checkbox = document.createElement('input')
                checkbox.type = 'checkbox'
                checkbox.id = 'projectionCheckbox'
                checkbox.classList.add('proj-checkbox')
                checkbox.checked = !this.isOpenMonthBilled
                checkbox.addEventListener('change', () => {
                    this.toggleOpenMonthCol()
                })
                const label = document.createElement('label')
                label.htmlFor = 'projectionCheckbox'
                label.textContent = 'Projections'

                cell.appendChild(div)
                div.appendChild(checkbox)
                div.appendChild(label)
            }
        }
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c] as any
            const rowItem = row.dataItem
            cell.classList.remove('green-cell')
            cell.classList.remove('dark-yellow-cell')
            cell.style.fontWeight = 'unset'

            const bindingParts = col._binding._parts
            if (bindingParts.length === 3 && rowItem.detailsByMonth) {
                const secondBinding = bindingParts[1].indexOf(' Billed') > -1 ? bindingParts[1].replace(' Billed', '') : bindingParts[1]
                const match = rowItem[bindingParts[0]][secondBinding]
                if (!match) return
                if (match.isYellow && this.active == 3) {
                    cell.classList.add('dark-yellow-cell')
                }
                if (match.isRevenueActual) {
                    // We show actual revenue in green. However, billed revenue depends on the date and isOpenMonthBilled.
                    if (this.active == 3) {
                        if (match.date.toDateString() == this.openMonth.toDateString()) {
                            if (this.isOpenMonthBilled) {
                                cell.classList.add('green-cell')
                            }
                        } else if (match.date < this.openMonth) {
                            cell.classList.add('green-cell')
                        }
                    } else {
                        cell.classList.add('green-cell')
                    }
                }
                if (match.billedProjection) {
                    cell.title = 'Projection: ' + this.currencyPipe.transform(match.billedProjection)
                }
            }
            if (col.binding == 'job' && !rowItem.detailsByMonth) {
                cell.style.fontWeight = '700'
            }

            if (col.binding.indexOf('Totals') > -1) {
                cell.style.fontWeight = '700'
            }

            if (rowItem.job.indexOf('Totals') > -1) {
                cell.style.fontWeight = '700'
            }
        }
    }
    toggleOpenMonthCol() {
        this.isOpenMonthBilled = !this.isOpenMonthBilled
        this.generateColumns()
    }
    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const row = flexGrid.rows[args.row]
        const col = flexGrid.columns[args.col] as any
        const rowItem = row.dataItem

        if (rowItem.fillOrTotalRow) {
            args.cancel = true
            return
        }

        const bindingParts = col._binding._parts
        if (bindingParts.length === 3 && rowItem.detailsByMonth) {
            const match = rowItem[bindingParts[0]][bindingParts[1]]
            if (match.isRevenueActual || this.active < 3) {
                if (this.active == 3) {
                    if (match.date.toDateString() == this.openMonth.toDateString()) {
                        if (this.isOpenMonthBilled) {
                            args.cancel = true
                        }
                    } else if (match.date < this.openMonth) {
                        args.cancel = true
                    }
                } else {
                    args.cancel = true
                }
            }
        }
    }
    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const row = flexGrid.rows[args.row]
        const col = flexGrid.columns[args.col] as any
        const rowItem = row.dataItem
        const bindingParts = col.binding.split('.')
        try {
            this.updateBilledProjection(
                rowItem.jobId,
                bindingParts[1],
                rowItem.detailsByMonth[bindingParts[1]].revenue,
                rowItem.isYellow ?? false,
            )
        } catch {
            this.services.notification.error('An error occured updating the projection. Refresh your browser to continue.')
        }
    }
    updateBilledProjection(job: string, date: string, projection: number, isYellow: boolean) {
        const payload = {
            job: job,
            date: date,
            projection: projection,
            isYellow: isYellow,
        }
        const match = this.rawData.filter(x => x.job == job && x.binding == date)[0]
        if (match) {
            match.billedProjection = projection
        } else {
            const jobData = this.rawData.filter(x => x.job == job)[0]
            this.rawData.push({
                job: job,
                description: 'PRWUA Deer Creek Tube Valve Replacement',
                jobType: jobData.jobType,
                jobContract: 0,
                revenue: 0,
                date: this.convertToDate(date),
                grossProfit: 0,
                isRevenueActual: false,
                actualRevenue: 0,
                projectedRevenue: 0,
                actualGrossProfit: 0,
                projectedGrossProfit: 0,
                billedAmount: 0,
                billedProjection: projection,
                projectManager: jobData.projectManager,
                customer: jobData.customer,
                binding: date,
            })
        }

        // When we regenerate the rows, the selection changes, so restore it,
        // and the selection will automatically advance as expected
        const currentSelection = this.grid.flexGrid.selection

        // If we pushed a new row to rawData, we need to update some other data.
        this.allRevenueData = this.rawData
        this.rebuildJobGroups()

        this.generateRows()
        this.grid.flexGrid.selection = currentSelection

        this.services.data.post('/api/Projections/UpdateBilledProjection', payload).subscribe({
            error: () => {
                this.services.notification.error('An error occured updating the projection.')
            },
        })
    }
    convertToDate(dateString: string) {
        const parts = dateString.split(' ')
        const yearCode = parts[1]
        const monthCode = parts[0]

        // Create an object to map month codes to month numbers
        const monthMap = {
            Jan: 0,
            Feb: 1,
            Mar: 2,
            Apr: 3,
            May: 4,
            Jun: 5,
            Jul: 6,
            Aug: 7,
            Sep: 8,
            Oct: 9,
            Nov: 10,
            Dec: 11,
        }

        // Convert the yearCode to a full year
        const fullYear = 2000 + parseInt(yearCode, 10)

        // Get the month number from the month code
        const monthNumber = monthMap[monthCode]

        // Create a JavaScript Date object with the obtained month and year
        const date = new Date(fullYear, monthNumber)

        return date
    }

    // Bonus Table functions
    refreshBonus() {
        const inputs = this.services.data.bonusTableYearlyVariables.items.find(x => x.year == this.selectedYear) as BonusTableYearlyVariable

        if (!inputs) {
            this.services.notification.error(
                'No data found for the selected year. Please enter a record for the selected year in the Bonus Table Inputs tab.',
            )
            return
        }

        const metrics = [] as any[]
        const planPercent = inputs?.planPercent
        const bonusPoolPercent = inputs?.bonusPoolPercent
        const bonusExpensePercent = 0.19
        const percents = [0.9, 0.95, 1, 1.02]
        const overhead = inputs?.overhead
        const plan = inputs?.plan
        const extra = inputs?.extra
        const otherIncome = inputs?.otherIncome

        metrics.push({
            metric: 'Sales',
            plan90: plan,
            plan95: plan,
            plan: plan,
            level2: plan,
            level3: plan,
            level4: plan,
            level5: plan,
            level6: plan,
            level7: plan,
            level8: extra,
        })

        metrics.push({
            metric: 'Gross Margin',
            plan90: plan * planPercent * percents[0],
            plan95: plan * planPercent * percents[1],
            plan: plan * planPercent * percents[2],
            level2: plan * planPercent * percents[3],
            level3: plan * planPercent * percents[3] * percents[3],
            level4: plan * planPercent * percents[3] * percents[3] * percents[3],
            level5: plan * planPercent * percents[3] * percents[3] * percents[3] * percents[3],
            level6: plan * planPercent * percents[3] * percents[3] * percents[3] * percents[3] * percents[3],
            level7: plan * planPercent * percents[3] * percents[3] * percents[3] * percents[3] * percents[3] * percents[3],
            level8: extra * planPercent,
        })

        metrics.push({
            metric: 'Overhead',
            plan90: overhead,
            plan95: overhead,
            plan: overhead,
            level2: overhead,
            level3: overhead,
            level4: overhead,
            level5: overhead,
            level6: overhead,
            level7: overhead,
            level8: overhead,
        })

        metrics.push({
            metric: 'GPBT',
            plan90: metrics[1].plan90 - metrics[2].plan90,
            plan95: metrics[1].plan95 - metrics[2].plan95,
            plan: metrics[1].plan - metrics[2].plan,
            level2: metrics[1].level2 - metrics[2].level2,
            level3: metrics[1].level3 - metrics[2].level3,
            level4: metrics[1].level4 - metrics[2].level4,
            level5: metrics[1].level5 - metrics[2].level5,
            level6: metrics[1].level6 - metrics[2].level6,
            level7: metrics[1].level7 - metrics[2].level7,
            level8: metrics[1].level8 - metrics[2].level8,
        })

        metrics.push({
            metric: 'Total Bonus Pool',
            plan90: metrics[3].plan90 * (bonusPoolPercent - 0.01),
            plan95: metrics[3].plan95 * (bonusPoolPercent - 0.005),
            plan: metrics[3].plan * bonusPoolPercent,
            level2: metrics[3].level2 * (bonusPoolPercent + 0.005),
            level3: metrics[3].level3 * (bonusPoolPercent + 0.01),
            level4: metrics[3].level4 * (bonusPoolPercent + 0.015),
            level5: metrics[3].level5 * (bonusPoolPercent + 0.02),
            level6: metrics[3].level6 * (bonusPoolPercent + 0.025),
            level7: metrics[3].level7 * (bonusPoolPercent + 0.03),
            level8: metrics[3].level8 * (bonusPoolPercent + 0.04),
        })

        metrics.push({
            metric: '50% 401k MATCH',
            plan90: '-',
            plan95: '-',
            plan: '-',
            level2: '-',
            level3: '-',
            level4: '-',
            level5: '-',
            level6: '-',
            level7: '-',
            level8: '-',
        })

        metrics.push({
            metric: 'Bonus Pool',
            plan90: metrics[4].plan90,
            plan95: metrics[4].plan95,
            plan: metrics[4].plan,
            level2: metrics[4].level2,
            level3: metrics[4].level3,
            level4: metrics[4].level4,
            level5: metrics[4].level5,
            level6: metrics[4].level6,
            level7: metrics[4].level7,
            level8: metrics[4].level8,
        })

        const percentPipe = new PercentPipe('en-US')
        metrics.push({
            metric: 'Bonus Pool %',
            plan90: percentPipe.transform(bonusPoolPercent - 0.01),
            plan95: percentPipe.transform(bonusPoolPercent - 0.005),
            plan: percentPipe.transform(bonusPoolPercent),
            level2: percentPipe.transform(bonusPoolPercent + 0.005),
            level3: percentPipe.transform(bonusPoolPercent + 0.01),
            level4: percentPipe.transform(bonusPoolPercent + 0.015),
            level5: percentPipe.transform(bonusPoolPercent + 0.02),
            level6: percentPipe.transform(bonusPoolPercent + 0.025),
            level7: percentPipe.transform(bonusPoolPercent + 0.03),
            level8: percentPipe.transform(bonusPoolPercent + 0.04),
        })

        metrics.push({
            metric: 'Bonus Expenses',
            plan90: metrics[6].plan90 * bonusExpensePercent,
            plan95: metrics[6].plan95 * bonusExpensePercent,
            plan: metrics[6].plan * bonusExpensePercent,
            level2: metrics[6].level2 * bonusExpensePercent,
            level3: metrics[6].level3 * bonusExpensePercent,
            level4: metrics[6].level4 * bonusExpensePercent,
            level5: metrics[6].level5 * bonusExpensePercent,
            level6: metrics[6].level6 * bonusExpensePercent,
            level7: metrics[6].level7 * bonusExpensePercent,
            level8: metrics[6].level8 * bonusExpensePercent,
        })

        metrics.push({
            metric: 'Other Income',
            plan90: otherIncome,
            plan95: otherIncome,
            plan: otherIncome,
            level2: otherIncome,
            level3: otherIncome,
            level4: otherIncome,
            level5: otherIncome,
            level6: otherIncome,
            level7: otherIncome,
            level8: otherIncome,
        })

        metrics.push({
            metric: 'Net Profit (after bonus)',
            plan90: metrics[3].plan90 - metrics[6].plan90 - metrics[8].plan90 + metrics[9].plan90,
            plan95: metrics[3].plan95 - metrics[6].plan95 - metrics[8].plan95 + metrics[9].plan95,
            plan: metrics[3].plan - metrics[6].plan - metrics[8].plan + metrics[9].plan,
            level2: metrics[3].level2 - metrics[6].level2 - metrics[8].level2 + metrics[9].level2,
            level3: metrics[3].level3 - metrics[6].level3 - metrics[8].level3 + metrics[9].level3,
            level4: metrics[3].level4 - metrics[6].level4 - metrics[8].level4 + metrics[9].level4,
            level5: metrics[3].level5 - metrics[6].level5 - metrics[8].level5 + metrics[9].level5,
            level6: metrics[3].level6 - metrics[6].level6 - metrics[8].level6 + metrics[9].level6,
            level7: metrics[3].level7 - metrics[6].level7 - metrics[8].level7 + metrics[9].level7,
            level8: metrics[3].level8 - metrics[6].level8 - metrics[8].level8 + metrics[9].level8,
        })

        metrics.push({
            metric: 'Owner Distribution',
            plan90: '',
            plan95: '',
            plan: percentPipe.transform(0.18),
            level2: percentPipe.transform(0.19),
            level3: percentPipe.transform(0.19),
            level4: percentPipe.transform(0.2),
            level5: percentPipe.transform(0.21),
            level6: percentPipe.transform(0.22),
            level7: percentPipe.transform(0.23),
            level8: percentPipe.transform(0.24),
        })

        metrics.push({
            metric: 'NPBT',
            plan90: '',
            plan95: '',
            plan: '',
            level2: '',
            level3: '',
            level4: '',
            level5: '',
            level6: '',
            level7: '',
            level8: '',
        })

        metrics.push({
            metric: 'ROS',
            plan90: percentPipe.transform(metrics[10].plan90 / metrics[0].plan90, '0.2-2'),
            plan95: percentPipe.transform(metrics[10].plan95 / metrics[0].plan95, '0.2-2'),
            plan: percentPipe.transform(metrics[10].plan / metrics[0].plan, '0.2-2'),
            level2: percentPipe.transform(metrics[10].level2 / metrics[0].level2, '0.2-2'),
            level3: percentPipe.transform(metrics[10].level3 / metrics[0].level3, '0.2-2'),
            level4: percentPipe.transform(metrics[10].level4 / metrics[0].level4, '0.2-2'),
            level5: percentPipe.transform(metrics[10].level5 / metrics[0].level5, '0.2-2'),
            level6: percentPipe.transform(metrics[10].level6 / metrics[0].level6, '0.2-2'),
            level7: percentPipe.transform(metrics[10].level7 / metrics[0].level7, '0.2-2'),
            level8: percentPipe.transform(metrics[10].level8 / metrics[0].level8, '0.2-2'),
        })

        metrics.push({
            metric: 'GM%',
            plan90: percentPipe.transform(metrics[1].plan90 / metrics[0].plan90, '0.2-2'),
            plan95: percentPipe.transform(metrics[1].plan95 / metrics[0].plan95, '0.2-2'),
            plan: percentPipe.transform(metrics[1].plan / metrics[0].plan, '0.2-2'),
            level2: percentPipe.transform(metrics[1].level2 / metrics[0].level2, '0.2-2'),
            level3: percentPipe.transform(metrics[1].level3 / metrics[0].level3, '0.2-2'),
            level4: percentPipe.transform(metrics[1].level4 / metrics[0].level4, '0.2-2'),
            level5: percentPipe.transform(metrics[1].level5 / metrics[0].level5, '0.2-2'),
            level6: percentPipe.transform(metrics[1].level6 / metrics[0].level6, '0.2-2'),
            level7: percentPipe.transform(metrics[1].level7 / metrics[0].level7, '0.2-2'),
            level8: percentPipe.transform(metrics[1].level8 / metrics[0].level8, '0.2-2'),
        })

        this.bonusGrid.items = metrics
    }
    bonusGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            cell.classList.remove('yellow-cell')
            cell.style.fontWeight = 'unset'
        }
    }
    jobStatusChanged() {
        this.writeStoredNumbers(
            ProjectionsComponent.JOB_STATUS_STORAGE_KEY,
            this.activeMap.filter(item => item.$checked).map(x => x.value),
        )
        this.refresh()
    }
    yearsChanged() {
        const selectedYears = this.availableYears.filter(x => x['checked']).map(x => x.value)
        this.persistedYearSelection = selectedYears
        this.writeStoredNumbers(ProjectionsComponent.YEARS_STORAGE_KEY, selectedYears)
        this.generateColumns()
    }
    private restoreJobStatusSelection() {
        const stored = this.readStoredNumbers(ProjectionsComponent.JOB_STATUS_STORAGE_KEY)
        const matching = stored?.filter(value => this.activeMap.some(item => item.value === value)) ?? []
        const statusesToCheck = matching.length > 0 ? matching : this.activeMap.map(item => item.value)
        for (const item of this.activeMap) {
            item.$checked = statusesToCheck.includes(item.value)
        }
    }
    private readStoredNumbers(key: string): number[] | null {
        try {
            const stored = localStorage.getItem(key)
            if (!stored) return null
            const parsed = JSON.parse(stored)
            if (!Array.isArray(parsed) || !parsed.every(value => typeof value === 'number')) return null
            return parsed
        } catch {
            return null
        }
    }
    private writeStoredNumbers(key: string, values: number[]) {
        try {
            localStorage.setItem(key, JSON.stringify(values))
        } catch {
            /* ignore storage failures */
        }
    }
    private hasNonZeroMonthValue(detailsByMonth: Record<string, { revenue?: number }>): boolean {
        for (const key in detailsByMonth) {
            if (key.indexOf('Totals') > -1) continue
            if ((detailsByMonth[key]?.revenue ?? 0) !== 0) return true
        }
        return false
    }
    calculateProgress() {
        const currentYear = (this.currentYear = new Date().getFullYear())
        const yearMatch = this.availableYears.find(x => x['checked'] && x.value == currentYear)
        if (!yearMatch) {
            this.currentYearTotalActualProfit = 0
            this.projectedProfit = 0
            this.progress = 0
            this.progress2 = 0
            this.nextYearGP = 0
            this.nextYearProgress = 0
            return
        }

        const thisYearDataForActuals = this.allRevenueData.filter(x => x.date.getFullYear() == currentYear && x.date < this.gaugeDate)
        const thisYearDataForProjections = this.allRevenueData.filter(x => x.date.getFullYear() == currentYear && x.date >= this.gaugeDate)

        const actualGrossProfits = thisYearDataForActuals.map(x => x.actualGrossProfit)
        this.currentYearTotalActualProfit = actualGrossProfits.length > 0 ? actualGrossProfits.reduce((a, b) => a + b) : 0
        this.projectedProfit = thisYearDataForProjections.map(x => x.projectedGrossProfit).reduce((a, b) => a + b)
        const actualPlusProjected = this.currentYearTotalActualProfit + this.projectedProfit

        const profitMetrics = this.bonusGrid.items.find(x => x.metric == 'Gross Margin')

        const btyv = this.services.data.bonusTableYearlyVariables.items.find(x => x.year == currentYear) as BonusTableYearlyVariable
        const goal = btyv.plan * btyv.planPercent

        this.progress = (this.currentYearTotalActualProfit / goal) * 100
        this.progress2 = (actualPlusProjected / goal) * 100

        this.ranges[0] = (profitMetrics.plan / profitMetrics.plan) * 100
        this.ranges[1] = (profitMetrics.level2 / profitMetrics.plan) * 100
        this.ranges[2] = (profitMetrics.level3 / profitMetrics.plan) * 100
        this.ranges[3] = (profitMetrics.level4 / profitMetrics.plan) * 100
        this.ranges[4] = (profitMetrics.level5 / profitMetrics.plan) * 100
        this.ranges[5] = (profitMetrics.level6 / profitMetrics.plan) * 100
        this.ranges[6] = (profitMetrics.level7 / profitMetrics.plan) * 100
        this.ranges[7] = (profitMetrics.level8 / profitMetrics.plan) * 100
        this.ranges[8] = (profitMetrics.level8 / profitMetrics.plan) * 100

        // Finally, we can do the next year projection
        const nextYearProjections = this.allRevenueData.filter(x => x.date.getFullYear() == currentYear + 1)
        const nextYearBtyv = this.services.data.bonusTableYearlyVariables.items.find(
            x => x.year == currentYear + 1,
        ) as BonusTableYearlyVariable
        if (!nextYearBtyv) return
        const nextYearGoal = nextYearBtyv.plan * nextYearBtyv.planPercent
        this.nextYearGP = nextYearProjections.map(x => x.projectedGrossProfit).reduce((a, b) => a + b)
        this.nextYearProgress = (this.nextYearGP / nextYearGoal) * 100
        //    console.log(this.ranges)
        //    console.log("level1: " + profitMetrics.plan)
        //    console.log("level2: " + profitMetrics.level2)
        //    console.log("level3: " + profitMetrics.level3)
        //    console.log("level4: " + profitMetrics.level4)
        //    console.log("level5: " + profitMetrics.level5)
        //    console.log("level6: " + profitMetrics.level6)
        //    console.log("level7: " + profitMetrics.level7)
        //    console.log("level8: " + profitMetrics.level8)
    }
    projectionStartMonthChanged() {
        this.refresh()
    }
    tabChanged() {
        // The reconciliation runs across every job rather than the Costs/Hours selection, so it
        // loads on its own instead of riding along with the cost data.
        if (this.active === 9) {
            this.refreshEarnedRevenueVariances()
        }
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
            if (this.active < 4) {
                this.generateColumns()
            }
        }, 50)
    }
    // Make it easy to lookup the month format we have chosen for our column binding (e.g. 'Jan 23')
    monthByInt = [
        { val: 0, display: 'Jan' },
        { val: 1, display: 'Feb' },
        { val: 2, display: 'Mar' },
        { val: 3, display: 'Apr' },
        { val: 4, display: 'May' },
        { val: 5, display: 'Jun' },
        { val: 6, display: 'Jul' },
        { val: 7, display: 'Aug' },
        { val: 8, display: 'Sep' },
        { val: 9, display: 'Oct' },
        { val: 10, display: 'Nov' },
        { val: 11, display: 'Dec' },
    ]

    costSeries: Record<string, string> = {
        actualLabor: 'Labor (Actual)',
        projectedLabor: 'Labor (Projected)',
        actualFsLabor: 'FS Labor (Actual)',
        projectedFsLabor: 'FS Labor (Projected)',
        actualNonFsLabor: 'Non-FS Labor (Actual)',
        projectedNonFsLabor: 'Non-FS Labor (Projected)',
        actualMaterial: 'Material (Actual)',
        projectedMaterial: 'Material (Projected)',
        actualEquipment: 'Equipment (Actual)',
        projectedEquipment: 'Equipment (Projected)',
        actualRentals: 'Rentals (Actual)',
        projectedRentals: 'Rentals (Projected)',
        actualSubcontractor: 'Subcontractor (Actual)',
        projectedSubcontractor: 'Subcontractor (Projected)',
        actualOther: 'Other (Actual)',
        projectedOther: 'Other (Projected)',
        actualOverhead: 'Overhead (Actual)',
        projectedOverhead: 'Overhead (Projected)',
        contingency: 'Contingency',
    }
}
